// src/store/slices/organizerSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { organizerAuthAPI, organizerEventsAPI } from "@/services/organizerApi";
import { setOrganizerAccessToken, clearOrganizerAccessToken } from "@/services/organizerApi";

// ==========================================
// AUTH THUNKS
// ==========================================

export const loginOrganizer = createAsyncThunk(
    "organizer/login",
    async (credentials, { rejectWithValue }) => {
        try {
            const response = await organizerAuthAPI.login(credentials);
            if (response.data?.token) {
                setOrganizerAccessToken(response.data.token);
            }
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Login failed" });
        }
    }
);

export const applyOrganizer = createAsyncThunk(
    "organizer/apply",
    async (applicationData, { rejectWithValue }) => {
        try {
            const response = await organizerAuthAPI.apply(applicationData);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Application failed" });
        }
    }
);

export const fetchOrganizerProfile = createAsyncThunk(
    "organizer/fetchProfile",
    async (_, { rejectWithValue }) => {
        try {
            const response = await organizerAuthAPI.getMe();
            return response.data?.organizer || response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to fetch profile" });
        }
    }
);

/**
 * Silent refresh for organizer - attempt to restore session on page load
 */
export const silentRefreshOrganizer = createAsyncThunk(
    "organizer/silentRefresh",
    async (_, { rejectWithValue }) => {
        try {
            const response = await organizerAuthAPI.refresh();
            const token = response.data?.data?.token;
            if (token) {
                setOrganizerAccessToken(token);
                return response.data;
            }
            return rejectWithValue({ message: "No token in refresh response" });
        } catch (err) {
            clearOrganizerAccessToken();
            return rejectWithValue(err.response?.data || { message: "Session expired" });
        }
    }
);

export const updateOrganizerPassword = createAsyncThunk(
    "organizer/updatePassword",
    async (passwordData, { rejectWithValue }) => {
        try {
            const response = await organizerAuthAPI.updatePassword(passwordData);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to update password" });
        }
    }
);

export const updateOrganizerEmail = createAsyncThunk(
    "organizer/updateEmail",
    async (emailData, { rejectWithValue }) => {
        try {
            const response = await organizerAuthAPI.updateEmail(emailData);
            return response.data?.organizer || response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to update email" });
        }
    }
);

export const uploadOrganizerLogo = createAsyncThunk(
    "organizer/uploadLogo",
    async (formData, { rejectWithValue }) => {
        try {
            const response = await organizerAuthAPI.uploadLogo(formData);
            return response.data; // Assuming it returns the updated organizer or logo URL
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to upload logo" });
        }
    }
);

export const removeOrganizerLogo = createAsyncThunk(
    "organizer/removeLogo",
    async (_, { rejectWithValue }) => {
        try {
            await organizerAuthAPI.removeLogo();
            return null;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to remove logo" });
        }
    }
);

// ==========================================
// EVENTS THUNKS
// ==========================================

export const fetchOrganizerEvents = createAsyncThunk(
    "organizer/fetchEvents",
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await organizerEventsAPI.getMyEvents(params);
            return {
                events: response.data?.events || [],
                component_counts: response.data?.component_counts || [],
                pagination: {
                    page: response.data?.pagination?.currentPage || 1,
                    pages: response.data?.pagination?.totalPages || 1,
                    total: response.data?.pagination?.totalDocuments || 0
                }
            };
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to fetch events" });
        }
    }
);

export const createEvent = createAsyncThunk(
    "organizer/createEvent",
    async (eventData, { rejectWithValue }) => {
        try {
            const response = await organizerEventsAPI.createEvent(eventData);
            return response.data?.event || response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to create event" });
        }
    }
);

export const fetchEventDetails = createAsyncThunk(
    "organizer/fetchEventDetails",
    async (eventId, { rejectWithValue }) => {
        try {
            const response = await organizerEventsAPI.getEvent(eventId);
            return response.data?.event || response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to fetch event details" });
        }
    }
);

export const updateEvent = createAsyncThunk(
    "organizer/updateEvent",
    async ({ eventId, eventData }, { rejectWithValue }) => {
        try {
            const response = await organizerEventsAPI.updateEvent(eventId, eventData);
            return response.data?.event || response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to update event" });
        }
    }
);

export const deleteEvent = createAsyncThunk(
    "organizer/deleteEvent",
    async (eventId, { rejectWithValue }) => {
        try {
            await organizerEventsAPI.deleteEvent(eventId);
            return eventId;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to delete event" });
        }
    }
);

export const submitEventForReview = createAsyncThunk(
    "organizer/submitEvent",
    async (eventId, { rejectWithValue }) => {
        try {
            const response = await organizerEventsAPI.submitEvent(eventId);
            return response.data?.event || response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to submit event" });
        }
    }
);

export const fetchEventRegistrations = createAsyncThunk(
    "organizer/fetchRegistrations",
    async (eventId, { rejectWithValue }) => {
        try {
            const response = await organizerEventsAPI.getEventRegistrations(eventId);
            return response.data?.registrations || response.data || [];
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to fetch registrations" });
        }
    }
);

// ==========================================
// SLICE
// ==========================================

const organizerSlice = createSlice({
    name: "organizer",
    initialState: {
        // Auth state
        organizer: null,
        token: null,
        isAuthenticated: false,

        // Events state
        events: [],
        eventsPagination: { page: 1, pages: 1, total: 0 },
        organizerComponentCounts: [],
        currentEvent: null,
        registrations: [],

        // Loading/Error state
        isLoading: true, // Start true for silent refresh check
        error: null,
        applicationSubmitted: false,
    },
    reducers: {
        logoutOrganizer: (state) => {
            state.organizer = null;
            state.token = null;
            state.isAuthenticated = false;
            state.events = [];
            state.currentEvent = null;
            state.registrations = [];
            clearOrganizerAccessToken();
        },
        clearOrganizerError: (state) => {
            state.error = null;
        },
        resetApplicationState: (state) => {
            state.applicationSubmitted = false;
        },
    },
    extraReducers: (builder) => {
        builder
            // LOGIN
            .addCase(loginOrganizer.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(loginOrganizer.fulfilled, (state, action) => {
                state.isLoading = false;
                state.organizer = action.payload.data?.organizer || action.payload.data;
                state.token = action.payload.data?.token;
                state.isAuthenticated = true;
            })
            .addCase(loginOrganizer.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Login failed";
            })

            // APPLY
            .addCase(applyOrganizer.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(applyOrganizer.fulfilled, (state) => {
                state.isLoading = false;
                state.applicationSubmitted = true;
            })
            .addCase(applyOrganizer.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Application failed";
            })

            // FETCH PROFILE
            .addCase(fetchOrganizerProfile.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchOrganizerProfile.fulfilled, (state, action) => {
                state.isLoading = false;
                state.organizer = action.payload;
                state.isAuthenticated = true;
            })
            .addCase(fetchOrganizerProfile.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Failed to fetch profile";
                if (action.payload?.status === 401) {
                    state.organizer = null;
                    state.token = null;
                    state.isAuthenticated = false;
                    clearOrganizerAccessToken();
                }
            })

            // SILENT REFRESH
            .addCase(silentRefreshOrganizer.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(silentRefreshOrganizer.fulfilled, (state, action) => {
                state.isLoading = false;
                state.token = action.payload?.data?.token;
                state.organizer = action.payload?.data?.organizer;
                state.isAuthenticated = true;
            })
            .addCase(silentRefreshOrganizer.rejected, (state) => {
                state.isLoading = false;
                state.isAuthenticated = false;
                state.organizer = null;
                state.token = null;
            })

            // UPDATE PASSWORD
            .addCase(updateOrganizerPassword.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateOrganizerPassword.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(updateOrganizerPassword.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Failed to update password";
            })

            // UPDATE EMAIL
            .addCase(updateOrganizerEmail.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateOrganizerEmail.fulfilled, (state, action) => {
                state.isLoading = false;
                if (action.payload) {
                    // Update the organizer's email in the state
                    state.organizer = { ...state.organizer, ...action.payload };
                }
            })
            .addCase(updateOrganizerEmail.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Failed to update email";
            })

            // UPLOAD LOGO
            .addCase(uploadOrganizerLogo.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(uploadOrganizerLogo.fulfilled, (state, action) => {
                state.isLoading = false;
                // Assuming payload contains updated organizer object or we locally update logo_url
                if (state.organizer && action.payload.logo_url) {
                    state.organizer.logo_url = action.payload.logo_url;
                } else if (action.payload.organizer) {
                    state.organizer = action.payload.organizer;
                }
            })
            .addCase(uploadOrganizerLogo.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Failed to upload logo";
            })

            // REMOVE LOGO
            .addCase(removeOrganizerLogo.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(removeOrganizerLogo.fulfilled, (state) => {
                state.isLoading = false;
                if (state.organizer) {
                    state.organizer.logo_url = null;
                }
            })
            .addCase(removeOrganizerLogo.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Failed to remove logo";
            })

            // FETCH EVENTS
            .addCase(fetchOrganizerEvents.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchOrganizerEvents.fulfilled, (state, action) => {
                state.isLoading = false;
                state.events = action.payload.events;
                state.eventsPagination = action.payload.pagination;
                state.organizerComponentCounts = action.payload.component_counts || [];
            })
            .addCase(fetchOrganizerEvents.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Failed to fetch events";
            })

            // CREATE EVENT
            .addCase(createEvent.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createEvent.fulfilled, (state, action) => {
                state.isLoading = false;
                state.events.unshift(action.payload);
            })
            .addCase(createEvent.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Failed to create event";
            })

            // FETCH EVENT DETAILS
            .addCase(fetchEventDetails.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchEventDetails.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentEvent = action.payload;
            })
            .addCase(fetchEventDetails.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Failed to fetch event details";
            })

            // UPDATE EVENT
            .addCase(updateEvent.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateEvent.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentEvent = action.payload;
                const index = state.events.findIndex(e => e._id === action.payload._id);
                if (index !== -1) {
                    state.events[index] = action.payload;
                }
            })
            .addCase(updateEvent.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Failed to update event";
            })

            // DELETE EVENT
            .addCase(deleteEvent.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteEvent.fulfilled, (state, action) => {
                state.isLoading = false;
                state.events = state.events.filter(e => e._id !== action.payload);
                if (state.currentEvent?._id === action.payload) {
                    state.currentEvent = null;
                }
            })
            .addCase(deleteEvent.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Failed to delete event";
            })

            // SUBMIT EVENT
            .addCase(submitEventForReview.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(submitEventForReview.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentEvent = action.payload;
                const index = state.events.findIndex(e => e._id === action.payload._id);
                if (index !== -1) {
                    state.events[index] = action.payload;
                }
            })
            .addCase(submitEventForReview.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Failed to submit event";
            })

            // FETCH REGISTRATIONS
            .addCase(fetchEventRegistrations.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchEventRegistrations.fulfilled, (state, action) => {
                state.isLoading = false;
                state.registrations = action.payload;
            })
            .addCase(fetchEventRegistrations.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Failed to fetch registrations";
            });
    },
});

export const { logoutOrganizer, clearOrganizerError, resetApplicationState } = organizerSlice.actions;
export default organizerSlice.reducer;
